import { request } from '@umijs/max';
import type { ClientListResponse } from './clients';
import type { TalentListResponse } from './talents';

export type ManagementKind = 'talents' | 'clients';

export interface ConsultantCollection {
  uuid: string;
  name: string;
  description?: string;
  parentUuid?: string | null;
  itemCount: number;
  children?: ConsultantCollection[];
}

export interface ConsultantCollectionItem {
  uuid: string;
  name: string;
  subtitle?: string;
}

export interface CollectionCopyResult {
  recipientCount: number;
  collectionCount: number;
  itemCount: number;
}

export interface ConsultantOption {
  memberUuid: string;
  userId: string;
  name: string;
  position?: string;
  departmentNames: string[];
  isCurrentMember: boolean;
  isAssignable: boolean;
}

export interface ConsultantManagementTreeNode {
  key: string;
  name: string;
  type: 'department' | 'consultant';
  memberUuid?: string;
  isCurrentMember?: boolean;
  isAssignable?: boolean;
  privatePoolCount?: number;
  children?: ConsultantManagementTreeNode[];
}

export interface ConsultantTransferResult {
  success: boolean;
  total: number;
  successCount: number;
  failCount: number;
  successList: string[];
  failList: Array<{ uuid: string; reason: string }>;
}

const basePath = '/bff/luole-hunter';

export const consultantPoolManagementApi = {
  getCollections: (kind: ManagementKind, memberUuid: string) =>
    request<ConsultantCollection[]>(`${basePath}/consultant-collections`, {
      params: { kind, memberUuid },
    }),
  getCollectionItems: (
    kind: ManagementKind,
    memberUuid: string,
    uuid: string,
    page: number,
  ) =>
    request<{ data: ConsultantCollectionItem[]; total: number }>(
      `${basePath}/consultant-collections/${encodeURIComponent(uuid)}/items`,
      { params: { kind, memberUuid, page } },
    ),
  copyCollections: (data: {
    kind: ManagementKind;
    memberUuid: string;
    collectionUuids: string[];
    targetMemberUuids: string[];
  }) =>
    request<CollectionCopyResult>(`${basePath}/consultant-collections/copy`, {
      method: 'POST',
      data,
    }),
  getConsultants: (kind: 'talents' | 'clients') =>
    request<{
      members: ConsultantOption[];
      tree: ConsultantManagementTreeNode[];
    }>(`${basePath}/${kind}/consultant-management/consultants`, {
      skipErrorHandler: true,
    }),
  getTalents: (params: { memberUuid: string; page: number; limit: number }) =>
    request<TalentListResponse>(
      `${basePath}/talents/consultant-management/talents`,
      { params },
    ),
  getClients: (params: { memberUuid: string; page: number; limit: number }) =>
    request<ClientListResponse>(
      `${basePath}/clients/consultant-management/clients`,
      { params },
    ),
  transferTalents: (data: {
    memberUuid: string;
    talentUuids: string[];
    targetMemberUuid: string | null;
    remark?: string;
  }) =>
    request<ConsultantTransferResult>(
      `${basePath}/talents/consultant-management/talents/transfer`,
      { method: 'POST', data },
    ),
  transferClients: (data: {
    memberUuid: string;
    clientUuids: string[];
    targetMemberUuid: string | null;
    remark?: string;
  }) =>
    request<ConsultantTransferResult>(
      `${basePath}/clients/consultant-management/clients/transfer`,
      { method: 'POST', data },
    ),
};
